import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

/** Same values persisted by the Appearance control on the tenant sidebar. */
export type TenantTheme = 'light' | 'dark' | 'system';
export type TenantResolvedTheme = 'light' | 'dark';

export const TENANT_THEME_STORAGE_KEY = 'theme';

export function readStoredTenantTheme(): TenantTheme {
  if (typeof window === 'undefined') return 'system';
  const saved = localStorage.getItem(TENANT_THEME_STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  return 'system';
}

export function resolveTenantTheme(theme: TenantTheme): TenantResolvedTheme {
  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/** Applies Tailwind `dark` class on `<html>` — matches Genie Client appearance behavior. */
export function applyTenantTheme(theme: TenantTheme): TenantResolvedTheme {
  const root = document.documentElement;
  const resolved = resolveTenantTheme(theme);

  if (theme === 'dark') {
    root.classList.add('dark');
    localStorage.setItem(TENANT_THEME_STORAGE_KEY, 'dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
    localStorage.setItem(TENANT_THEME_STORAGE_KEY, 'light');
  } else {
    localStorage.removeItem(TENANT_THEME_STORAGE_KEY);
    if (resolved === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }

  return resolved;
}

interface TenantAppearanceContextValue {
  theme: TenantTheme;
  resolvedTheme: TenantResolvedTheme;
  setTheme: (theme: TenantTheme) => void;
}

const TenantAppearanceContext = createContext<TenantAppearanceContextValue | null>(
  null,
);

export const TenantAppearanceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<TenantTheme>(() => readStoredTenantTheme());
  const [resolvedTheme, setResolvedTheme] = useState<TenantResolvedTheme>(() =>
    resolveTenantTheme(readStoredTenantTheme()),
  );

  const setTheme = useCallback((next: TenantTheme) => {
    setThemeState(next);
    setResolvedTheme(applyTenantTheme(next));
  }, []);

  useLayoutEffect(() => {
    setResolvedTheme(applyTenantTheme(theme));
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setResolvedTheme(applyTenantTheme('system'));
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== TENANT_THEME_STORAGE_KEY && event.key !== null) return;
      const stored = readStoredTenantTheme();
      setThemeState(stored);
      setResolvedTheme(applyTenantTheme(stored));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return (
    <TenantAppearanceContext.Provider value={value}>
      {children}
    </TenantAppearanceContext.Provider>
  );
};

export function useTenantAppearance(): TenantAppearanceContextValue {
  const value = useContext(TenantAppearanceContext);
  if (!value) {
    throw new Error('useTenantAppearance must be used within TenantAppearanceProvider');
  }
  return value;
}
